import React from 'react';
import { Box, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { AgentActivityLog, AgentActivityLogReviewStatus } from '@trnsact/trnsact-shared-types';
import { DateFormats } from 'constants/common';
import { SmartIntakeHistoryOutcome } from './SmartIntakeTypes';
import { buildSubmissionLink } from './SmartIntakeMapper';

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow = ({ label, value }: DetailRowProps) => (
  <Stack direction="row" spacing={1}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Stack>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
  <Stack spacing={1}>
    <Typography variant="subtitle1" fontWeight="bold">
      {title}
    </Typography>
    <Stack spacing={0.75}>{children}</Stack>
  </Stack>
);

interface TimelineEntry {
  label: string;
  dateTime: string;
}

function formatDateTime(value: string | null | undefined): string | null {
  return value ? moment(value).format(DateFormats.DateTimeShort12Hour) : null;
}

function buildTimeline(activity: AgentActivityLog): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  const startedAt = (activity.trigger as Record<string, unknown> | null)?.started_at as string | undefined;
  if (startedAt) entries.push({ label: 'Started', dateTime: startedAt });
  if (activity.createdDateTime) entries.push({ label: 'Parsed', dateTime: activity.createdDateTime });
  if (activity.review?.reviewedDateTime) {
    const label =
      activity.review.status === AgentActivityLogReviewStatus.Confirmed
        ? 'Confirmed'
        : activity.review.status === AgentActivityLogReviewStatus.Rejected
          ? 'Rejected'
          : 'Dismissed';
    entries.push({ label, dateTime: activity.review.reviewedDateTime });
  }
  return entries
    .map(entry => ({ ...entry, dateTime: entry.dateTime }))
    .sort((a, b) => moment(a.dateTime).valueOf() - moment(b.dateTime).valueOf());
}

interface SmartIntakeDetailsDrawerProps {
  activity: AgentActivityLog | null;
  outcome: SmartIntakeHistoryOutcome | null;
  onClose: () => void;
}

export const SmartIntakeDetailsDrawer = ({ activity, outcome, onClose }: SmartIntakeDetailsDrawerProps) => {
  const trigger = (activity?.trigger as Record<string, unknown> | null) ?? null;
  const payload = (activity?.payload as Record<string, unknown> | null) ?? null;
  const error = (activity?.error as Record<string, unknown> | null) ?? null;
  const extraction = payload?.detailed_extraction as Record<string, { value: unknown } | undefined> | null | undefined;
  const submission = activity ? buildSubmissionLink(activity.review ?? null) : null;

  return (
    <Drawer anchor="right" open={activity != null} onClose={onClose} PaperProps={{ sx: { width: 480 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Typography variant="h6">Submission Details</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {activity && (
        <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
          <Stack spacing={3}>
            <Section title="Source Details">
              <DetailRow label="From" value={trigger?.email_from as string | undefined} />
              <DetailRow label="Subject" value={trigger?.email_subject as string | undefined} />
              <DetailRow label="Received" value={formatDateTime(trigger?.email_date as string | undefined)} />
              <DetailRow label="Uploaded by" value={trigger?.user_uploaded_decision_letter as string | undefined} />
            </Section>

            <Section title="Processing Details">
              <DetailRow
                label="Extraction Confidence"
                value={
                  trigger?.extraction_confidence_score != null
                    ? `${Math.round((trigger.extraction_confidence_score as number) * 100)}%`
                    : null
                }
              />
              <DetailRow label="Business Name" value={extraction?.businessName?.value as string | undefined} />
              {outcome === 'failed' && (
                <DetailRow
                  label="Error"
                  value={
                    (error?.human_readable_message as string | undefined) ??
                    (error?.messages as string[] | undefined)?.join('; ')
                  }
                />
              )}
            </Section>

            {outcome !== 'failed' && (
              <Section title="Review Details">
                <DetailRow label="Status" value={activity.review?.status} />
                <DetailRow label="Reviewed by" value={activity.review?.reviewedByName} />
                <DetailRow label="Reviewed on" value={formatDateTime(activity.review?.reviewedDateTime)} />
                {activity.review?.fieldEdits && activity.review.fieldEdits.length > 0 && (
                  <DetailRow label="Fields edited" value={activity.review.fieldEdits.length} />
                )}
                {submission && (
                  <DetailRow
                    label="Submission"
                    value={submission.url ? <a href={submission.url}>{submission.label}</a> : submission.label}
                  />
                )}
              </Section>
            )}

            <Section title="Activity History">
              {buildTimeline(activity).map(entry => (
                <DetailRow key={entry.label} label={entry.label} value={formatDateTime(entry.dateTime)} />
              ))}
            </Section>
          </Stack>
        </Box>
      )}
    </Drawer>
  );
};
